import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { auth, firestore } from '../firebaseConfig';
import { buildFirestoreTransaction } from '../domain/finance';
import { awaitRemoteConfirmation, requireConnectionForMutation } from './connectivityService';

const encoder = new TextEncoder();

export const createTransactionShareLinkId = async ({
  sourceAccountId,
  sourceKind,
  sourceItemId,
  targetAccountId,
}) => {
  const input = JSON.stringify([sourceAccountId, sourceKind, sourceItemId, targetAccountId]);
  if (!window.crypto?.subtle) throw new Error('SHARE_HASH_UNAVAILABLE');
  const digest = await window.crypto.subtle.digest('SHA-256', encoder.encode(input));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const timestampLike = (value) => value instanceof Date
  || (value && typeof value === 'object' && typeof value.toMillis === 'function');

export const shareTransactionToAccount = async ({
  transaction,
  sourceAccountId,
  targetAccountId,
  attributedToUid,
  attributedToNameSnapshot,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  const actorUid = auth.currentUser?.uid;
  const sourceKind = transaction?.occurrenceKey ? 'recurringOccurrence' : 'transaction';
  const sourceItemId = sourceKind === 'recurringOccurrence' ? transaction?.occurrenceKey : transaction?.id;
  if (!actorUid || !sourceItemId || !sourceAccountId || !targetAccountId || sourceAccountId === targetAccountId) {
    throw new Error('INVALID_TRANSACTION_SHARE');
  }

  const financial = buildFirestoreTransaction(transaction);
  if (!financial) throw new Error('INVALID_TRANSACTION');
  delete financial.sharing;
  delete financial.attributedToUid;
  delete financial.attributedToNameSnapshot;
  const linkId = await createTransactionShareLinkId({ sourceAccountId, sourceKind, sourceItemId, targetAccountId });
  const transactionId = `share_${linkId}`;
  const targetRef = doc(firestoreDb, 'accounts', targetAccountId, 'transactions', transactionId);
  const timestamp = serverTimestamp();
  const sharing = {
    mode: 'snapshot', linkId, sourceAccountId, sourceKind, sourceItemId,
    sharedByUid: actorUid, sharedAt: timestamp, version: 1,
    ...(timestampLike(transaction.updatedAt) ? { sourceUpdatedAtSnapshot: transaction.updatedAt } : {}),
  };
  const payload = {
    ...financial,
    createdByUid: actorUid,
    ...(attributedToUid ? { attributedToUid } : {}),
    ...(attributedToNameSnapshot ? { attributedToNameSnapshot } : {}),
    sharing,
  };

  const result = await awaitRemoteConfirmation(runTransaction(firestoreDb, async (atomic) => {
    const existing = await atomic.get(targetRef);
    if (existing.exists()) {
      const existingSharing = existing.data()?.sharing;
      if (existingSharing?.linkId !== linkId || existingSharing?.sourceAccountId !== sourceAccountId
        || existingSharing?.sourceKind !== sourceKind || existingSharing?.sourceItemId !== sourceItemId) {
        throw new Error('TRANSACTION_SHARE_CONFLICT');
      }
      return { alreadyShared: true };
    }
    atomic.set(targetRef, { ...payload, createdAt: timestamp, updatedAt: timestamp });
    return { alreadyShared: false };
  }));

  return { transactionId, accountId: targetAccountId, linkId, ...result };
};
